import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { Task } from '@shared/models/task.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskType } from '@shared/enums/task-type.enum';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { EmployeeSearchItem } from '@shared/models/employee-search-item.model';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { DndModule } from 'ngx-drag-drop';
import { UIModule } from '@shared/modules/ui/ui.module';
import { SharedModule } from '@shared/shared.module';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { publicFile } from '@shared/helpers/public-file.helper';

@Component({
  selector: 'list-tasks-v2-single-task',
  templateUrl: './list-tasks-v2-single-task.component.html',
  styleUrls: ['./list-tasks-v2-single-task.component.scss'],
  host: { class: 'list-tasks-single-task-wrapper' },
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NgClass, DndModule, UIModule, SharedModule, MatCheckboxModule]
})
export class ListTasksV2SingleTaskComponent {
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  task = input.required<Task>();
  groupId = input.required<number>();
  isProcessGroup = input<boolean>(false);
  status = input<'created' | 'completed' | 'cancelled'>('created');
  parentTask = input<Task | null>(null);

  statusUpdated = output<{ task: Task; subtasksCompleted?: boolean }>();
  taskClicked = output<{ task: Task; groupId: number; isProcessGroup: boolean }>();

  protected readonly TaskStatus = TaskStatus;
  protected readonly TaskType = TaskType;
  protected readonly taskSidenavController = new TaskSidenavController();
  private readonly processTypeTranslationMap: Record<string, string> = {
    task: 'ProjectsDashboard.stageTasks.taskTypes.task',
    documentation: 'ProjectsDashboard.stageTasks.taskTypes.documentation',
    finance: 'ProjectsDashboard.stageTasks.taskTypes.finance',
    control: 'ProjectsDashboard.stageTasks.taskTypes.control',
    message: 'ProjectsDashboard.stageTasks.taskTypes.message'
  };

  private readonly typeClassMap: Record<string, string> = {
    task: 'type-task',
    note: 'type-note',
    order: 'type-order',
    correction: 'type-correction',
    change: 'type-change',
    documentation: 'type-documentation',
    finance: 'type-finance',
    control: 'type-control',
    message: 'type-message'
  };

  protected get isSubtask(): boolean {
    return !!this.parentTask();
  }

  protected showProcessDeadlineTag = computed<boolean>(
    () => !!this.isProcessGroup() && !!this.task()?.termEnd
  );

  protected isProcessMessageTask = computed<boolean>(
    () => !!this.isProcessGroup() && String((this.task() as any)?.type || '') === 'message'
  );

  protected isProcessMessageSent = computed<boolean>(
    () => this.isProcessMessageTask() && !!this.task()?.mailSentAt && !!this.task()?.mailSentBy
  );

  protected isProcessMessageScheduled = computed<boolean>(
    () => this.isProcessMessageTask() && !!this.task()?.mailScheduledAt
  );

  protected isCompleted = computed<boolean>(() => this.task()?.status === TaskStatus.COMPLETED);

  protected isCancelled = computed<boolean>(() => this.task()?.status === TaskStatus.CANCELLED);

  protected shouldBeDraggable = computed<boolean>(() => {
    if (this.isProcessGroup()) return false;
    return !this.isSubtask && !this.isCompleted() && !this.isCancelled();
  });

  protected get assignedUsers(): (string | EmployeeSearchItem)[] {
    const task = this.task();
    return [...(task?.assignedTo || []), ...(task?.assignedEmails || [])];
  }

  protected get processMessageSenderName(): string {
    const sender = (this.task() as any)?.mailSentBy || {};
    const fullName = String(sender?.fullName || '').trim();
    if (fullName) return fullName;

    const fromUserPerson = [sender?.userPerson?.firstName, sender?.userPerson?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (fromUserPerson) return fromUserPerson;

    return [sender?.name, sender?.surname].filter(Boolean).join(' ').trim();
  }

  protected get processMessageSenderAvatar(): string | null {
    const sender = this.task()?.mailSentBy || {};
    return publicFile(sender?.userPerson?.photo, 100);
  }

  protected identify(_index: number, { id }: Task): number {
    return id;
  }

  protected toggleSubtasks(): void {
    const task = this.task();
    task.openedSubtasks = !task.openedSubtasks;
    this.cdr.detectChanges();
  }

  protected previewTask(event: MouseEvent): void {
    event.preventDefault();
    const task = this.task();
    if (!task?.project?.id || (!task?.taskId && !task?.id)) return;

    if (this.isProcessGroup() && String((task as any)?.type || '') === TaskType.TYPE_MESSAGE) {
      this.taskClicked.emit({ task, groupId: this.groupId(), isProcessGroup: true });
      return;
    }

    this.ngZone.run(() => {
      this.taskSidenavController.previewTask(task?.taskId || task?.id?.toString(), {
        type: task?.type || TaskType.TYPE_TASK,
        isProcessTaskContext: this.isProcessGroup(),
        groupType: this.isProcessGroup() ? 'activity-template' : undefined,
        activityTemplateTaskType: this.isProcessGroup() ? task?.type || TaskType.TYPE_TASK : null
      });
    });
  }

  protected onAssignSearchDropdownTaskChange(task: Task): void {
    Object.assign(this.task(), task);
    this.cdr.detectChanges();
  }

  protected childStatus(task: Task): 'created' | 'completed' | 'cancelled' {
    if (task?.status === TaskStatus.COMPLETED) return 'completed';
    if (task?.status === TaskStatus.CANCELLED) return 'cancelled';
    return 'created';
  }

  protected getTaskTypeTranslationKey(): string {
    const type = this.task()?.type;
    if (!type) return '';
    return this.processTypeTranslationMap[type] || `Tasks.TaskType.${type}`;
  }

  protected getTaskTypeCssClass(): string {
    const type = this.task()?.type;
    if (!type) return '';
    return this.typeClassMap[type] || '';
  }

  protected onStatusChange(event: MatCheckboxChange): void {
    const checked = !!event.checked;
    const task = this.task();
    if (!task) return;

    if (checked) {
      task.status = TaskStatus.COMPLETED;
      this.statusUpdated.emit({ task, subtasksCompleted: false });
    } else {
      task.status = TaskStatus.CREATED;
      this.statusUpdated.emit({ task, subtasksCompleted: false });
    }

    this.updateCompletedChildrenCount(task);
    this.updateCompletedChildrenCount(this.parentTask());
    this.cdr.detectChanges();
  }

  private setCompletedForSubtasks(task: Task): void {
    (task.children || []).forEach((child: Task) => {
      child.status = TaskStatus.COMPLETED;
      this.setCompletedForSubtasks(child);
      this.updateCompletedChildrenCount(child);
    });
  }

  private updateCompletedChildrenCount(task: Task | null): void {
    if (!task) return;
    task.completedChildrenCount = (task.children || []).filter(
      (child: Task) => child.status === TaskStatus.COMPLETED
    ).length;
  }
}
